import { useEffect, useState } from 'react'

import { FieldError, Form, Submit, TextField, useForm } from '@cedarjs/forms'

import { MetadataItemProps } from 'src/common/types'
import Button from 'src/components/Custom/Button/Button'
import Dropdown from 'src/components/Custom/Dropdown/Dropdown'
import Loader from 'src/components/Custom/Loader/Loader'
import useWarnUnsavedChanges from 'src/lib/hooks/useWarnUnsavedChanges'

type PbsStationManagerFieldProps = {
  loadingData: boolean
  onChange?: (value: string | null, key?: string) => void
  onSave: () => Promise<void>
  selectedData: string
  stationManagerLivestreamOptions: MetadataItemProps[]
  validationRules: {
    validate?: {
      [key: string]: (value: string) => boolean | string
    }
  }
}

const PbsStationManagerField = ({
  loadingData,
  onChange,
  onSave,
  selectedData,
  stationManagerLivestreamOptions,
  validationRules,
}: PbsStationManagerFieldProps) => {
  const fieldName = 'pbsStationManagerLivestreamFeedCid'
  const [editable, setEditable] = useState(false)
  const [originalValues, setOriginalValues] = useState({
    pbsStationManager: '',
  })

  const formMethods = useForm()
  const setUnsaved = useWarnUnsavedChanges()

  useEffect(() => {
    const subscription = formMethods.watch((value, { name }) => {
      if (
        name == fieldName &&
        Object.prototype.hasOwnProperty.call(value, name)
      ) {
        setUnsaved(true)
        onChange(value[name], 'pbsStationManager')
      }
    })
    return () => subscription.unsubscribe()
  }, [formMethods, formMethods.watch, onChange, setUnsaved])

  const getButtonText = () => {
    const selectedOption = stationManagerLivestreamOptions
      .filter((item) => item.value == selectedData)
      ?.at(0)
    return selectedOption?.label || 'Select livestream feed'
  }

  const toggleEditable = () => {
    if (!editable) {
      setOriginalValues({
        pbsStationManager: selectedData,
      })
    }
    setEditable(!editable)
  }

  const handleCancel = () => {
    setEditable(false)
    for (const [key, value] of Object.entries(originalValues)) {
      onChange(value, key)
    }
    setUnsaved(false)

    formMethods.reset()
  }

  const onFormSubmit = () => {
    onSave()
    setUnsaved(false)
    setEditable(false)
  }

  return (
    <>
      <Form formMethods={formMethods} onSubmit={onFormSubmit}>
        {editable ? (
          <div className="flex">
            <div className="w-full">
              <div className="my-1 flex items-center justify-between py-1.5">
                <Dropdown
                  buttonText={getButtonText()}
                  fullWidth={true}
                  options={stationManagerLivestreamOptions}
                  onOptionsChange={({ value }) => {
                    formMethods.setValue(fieldName, value)
                  }}
                />
              </div>
              <TextField
                name={fieldName}
                value={selectedData}
                readOnly={true}
                validation={validationRules}
                className="hidden w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 read-only:ring-0 focus:ring-2 focus:ring-inset focus:ring-indigo-600 read-only:focus:ring-0 sm:text-sm sm:leading-6"
              />
              <FieldError name={fieldName} className="text-red-700" />
            </div>
            <div className="flex flex-col justify-center">
              <Submit
                disabled={loadingData}
                className="border-0 text-indigo-700"
              >
                {loadingData ? <Loader /> : 'Save'}
              </Submit>

              <Button
                title="Cancel"
                customButtonClass="border-0 text-indigo-700"
                onClick={handleCancel}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            {selectedData ? (
              <p>{selectedData}</p>
            ) : (
              <p>
                <em>Not set</em>
              </p>
            )}
            <Button
              title="Update"
              customButtonClass="border-0 text-indigo-700"
              onClick={toggleEditable}
            />
          </div>
        )}
      </Form>
    </>
  )
}

export default PbsStationManagerField
